/**
 * ChatSidebar
 *
 * Compact chat interface for the right sidebar of the workspace.
 * Uses the existing ChatInterface but with a streamlined layout.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../../../state/AppState';
import { useRetrievalExperience } from '../../../hooks/useRetrievalExperience';
import {
  createAgentPlan,
  createAgentSession,
  executeAgentPlanStream,
  getAgentPlan,
  interveneAgent,
  type AgentChatStreamEvent,
  type AgentCitation,
  type AgentAction,
  type AgentPlanResponse,
  type AgentPlanStep,
} from '../../../api/services/agent';
import { useWorkspace } from '../artifacts';
import type { WorkspaceMessage } from '../types';
import type { EmailArtifact } from '../artifacts';
import type {
  RetrievalPlan as RetrievalPlanState,
  RetrievalPlanStep as RetrievalPlanStateStep,
} from '../../../types/retrieval';
import { getDocumentContent } from '../../../api/services/knowledge';
import { MarkdownContent } from '../../common/MarkdownContent';
import { AssistantStatusIndicator } from '../../common/AssistantStatusIndicator';
import { GroundingSection } from '../grounding';
import { useInvalidateUsageQueries } from '../../../api/hooks/settings.hook';

interface ChatSidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
  isMobile?: boolean;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  isCollapsed = false,
  onToggle,
  isMobile = false,
}) => {
  const navigate = useNavigate();
  const { conversation, setConversation } = useAppState();
  const {
    state: workspaceState,
    createDocument,
    createEmail,
    refreshArtifacts,
    openArtifact,
    setOpenCitation,
  } = useWorkspace();
  const {
    state: retrievalState,
    startQuery,
    setSessionId: setRetrievalSessionId,
    setPlan,
    ingestEvent,
    focusCitationRef,
    reset: resetRetrievalState,
    requestPlanCommand,
    acknowledgePlanCommand,
    setExecutionPhase,
  } = useRetrievalExperience();

  const normalizeArtifactId = useCallback((raw: string) => {
    return raw.replace(/^artifact:/, '').replace(/:[0-9]+$/, '');
  }, []);

  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachedArtifacts, setAttachedArtifacts] = useState<Array<{ id: string; title: string }>>(
    [],
  );
  const [showArtifactPicker, setShowArtifactPicker] = useState(false);
  const [artifactSearch, setArtifactSearch] = useState('');
  const [streamSessionId, setStreamSessionId] = useState<string | null>(null);
  const [isPlanCommandRunning, setIsPlanCommandRunning] = useState(false);
  const [planCardExpanded, setPlanCardExpanded] = useState(false);
  const planExecutionPayloadRef = useRef<
    Record<string, { workspaceId: string; documentIds: string[]; retrievalLimit: number }>
  >({});
  const activeExecutionRef = useRef<{
    planId: string;
    abortController: AbortController;
  } | null>(null);

  const streamStatus = retrievalState.streamStatus;
  const streamStatusLabel =
    streamStatus === 'streaming'
      ? 'Streaming'
      : streamStatus === 'completed'
        ? 'Verified'
        : streamStatus === 'error'
          ? 'Issue'
          : 'Idle';
  const streamStatusClasses =
    streamStatus === 'streaming'
      ? 'bg-blue-100 text-blue-700 border-blue-200'
      : streamStatus === 'completed'
        ? 'bg-green-100 text-green-700 border-green-200'
        : streamStatus === 'error'
          ? 'bg-red-100 text-red-700 border-red-200'
          : 'bg-neutral-100 text-neutral-500 border-neutral-200';

  const planProgress = React.useMemo(() => {
    const steps = retrievalState.plan?.steps || [];
    if (steps.length === 0) {
      return { completed: 0, total: 0, percent: 0 };
    }
    const completed = steps.filter(
      (step) => step.status === 'completed' || step.status === 'succeeded',
    ).length;
    return {
      completed,
      total: steps.length,
      percent: Math.round((completed / steps.length) * 100),
    };
  }, [retrievalState.plan?.steps]);

  useEffect(() => {
    if (
      retrievalState.executionPhase === 'planned' ||
      retrievalState.executionPhase === 'executing'
    ) {
      setPlanCardExpanded(true);
    }
  }, [retrievalState.executionPhase]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const invalidateUsageQueries = useInvalidateUsageQueries();

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowArtifactPicker(false);
      }
    };

    if (showArtifactPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showArtifactPicker]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.messages]);

  // Focus input on mount
  useEffect(() => {
    if (!isCollapsed) {
      inputRef.current?.focus();
    }
  }, [isCollapsed]);

  // Pre-fill input when a NEEDS INPUT citation tag is clicked
  useEffect(() => {
    const handleFillInput = (rawEvent: Event) => {
      const event = rawEvent as CustomEvent<{ text?: string }>;
      const text = event.detail?.text;
      if (!text) return;
      setInputValue(text);
      window.setTimeout(() => {
        inputRef.current?.focus();
        // Place cursor at end
        const ta = inputRef.current;
        if (ta) {
          ta.selectionStart = ta.selectionEnd = ta.value.length;
        }
      }, 50);
    };
    window.addEventListener('curious:fill-input', handleFillInput as EventListener);
    return () => window.removeEventListener('curious:fill-input', handleFillInput as EventListener);
  }, []);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height based on scrollHeight, with min and max constraints
      const minHeight = 48; // min-h-[48px]
      const maxHeight = 150; // max-h-[150px]
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  const handleArtifactSelect = (artifact: { id: string; title: string }) => {
    if (!attachedArtifacts.find((a) => a.id === artifact.id)) {
      setAttachedArtifacts((prev) => [...prev, artifact]);
    }
    setShowArtifactPicker(false);
    setArtifactSearch('');
  };

  const removeAttachment = (index: number) => {
    setAttachedArtifacts((prev) => prev.filter((_, i) => i !== index));
  };

  // Process agent action - create or update artifact in workspace
  const processAgentAction = useCallback(
    (action: AgentAction | null | undefined, citations?: AgentCitation[]) => {
      if (!action) return;

      // normalize params to a loose record so we can runtime-check fields
      const params = (action.parameters ?? {}) as unknown as Record<string, unknown>;

      // Handle create_artifact action
      if (action.name === 'create_artifact' || action.name === 'draft_document') {
        const title =
          (typeof params.title === 'string' && params.title) ||
          (typeof params.subject === 'string' && params.subject) ||
          'Generated Document';

        let content = '';
        const rawContent =
          (params as Record<string, unknown>)['content'] ??
          (params as Record<string, unknown>)['body'] ??
          '';
        if (typeof rawContent === 'string') {
          content = rawContent;
        } else if (
          rawContent &&
          typeof rawContent === 'object' &&
          'markdown' in (rawContent as Record<string, unknown>)
        ) {
          content = String((rawContent as Record<string, unknown>)['markdown']);
        } else {
          content = String(rawContent ?? '');
        }

        createDocument(title, content, 'agent');
      }

      // Handle email / send_email / draft_email action
      if (
        action.name === 'email' ||
        action.name === 'send_email' ||
        action.name === 'draft_email'
      ) {
        const title = (typeof params.subject === 'string' && params.subject) || 'Email Draft';

        const toRaw = (params as Record<string, unknown>)['to'];
        let to: string[] = [];
        if (Array.isArray(toRaw)) {
          to = toRaw as string[];
        } else if (toRaw) {
          to = [toRaw as string];
        }

        const ccRaw = (params as Record<string, unknown>)['cc'];
        const cc = ccRaw
          ? Array.isArray(ccRaw)
            ? (ccRaw as string[])
            : [ccRaw as string]
          : undefined;

        const subject = typeof params.subject === 'string' ? params.subject : '';

        let body = '';
        const rawBody =
          (params as Record<string, unknown>)['body'] ??
          (params as Record<string, unknown>)['content'] ??
          '';
        if (typeof rawBody === 'string') {
          body = rawBody;
        } else if (
          rawBody &&
          typeof rawBody === 'object' &&
          'markdown' in (rawBody as Record<string, unknown>)
        ) {
          body = String((rawBody as Record<string, unknown>)['markdown']);
        } else {
          body = String(rawBody ?? '');
        }

        const emailData: Partial<
          Omit<EmailArtifact, 'id' | 'type' | 'createdAt' | 'updatedAt' | 'createdBy' | 'status'>
        > = {
          to,
          cc,
          subject,
          body,
          citations,
        };

        createEmail(title, emailData, 'agent');
      }

      // Handle update_artifact / update_email / read_artifact / open_artifact - refresh from backend
      if (
        action.name === 'update_artifact' ||
        action.name === 'update_email' ||
        action.name === 'read_artifact' ||
        action.name === 'open_artifact'
      ) {
        // Refresh artifacts to get the latest state from backend
        refreshArtifacts();

        // If there's an artifact_id, open it in the workspace
        const artifactId = action.parameters?.artifact_id;
        if (artifactId && typeof artifactId === 'string') {
          openArtifact(normalizeArtifactId(artifactId));
        }
      }
    },
    [createDocument, createEmail, refreshArtifacts, openArtifact, normalizeArtifactId],
  );

  const normalizeStreamCitation = useCallback((raw: any): AgentCitation | null => {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const citationRef = (raw.citation_ref as string | undefined) ?? '';
    const path = (raw.path as string | undefined) ?? citationRef ?? 'source';
    return {
      path,
      start_line: Number(raw.start_line ?? raw.start_pos ?? 0),
      end_line: Number(raw.end_line ?? raw.end_pos ?? raw.start_line ?? raw.start_pos ?? 0),
      text:
        (raw.text as string | undefined) ?? (raw.quote as string | undefined) ?? citationRef ?? '',
    };
  }, []);

  const normalizeGap = useCallback((raw: unknown): string | null => {
    if (!raw) {
      return null;
    }
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    if (typeof raw === 'object' && raw !== null) {
      const asRecord = raw as Record<string, unknown>;
      const value =
        (asRecord.description as string | undefined) ||
        (asRecord.gap as string | undefined) ||
        null;
      if (value && value.trim().length > 0) {
        return value.trim();
      }
    }
    return null;
  }, []);

  const scoreFromConfidenceSummary = useCallback((summary: unknown): number | undefined => {
    if (!summary || typeof summary !== 'object') {
      return undefined;
    }
    const values = summary as Record<string, unknown>;
    const grounded = typeof values.grounded === 'number' ? values.grounded : 0;
    const weak = typeof values.weak === 'number' ? values.weak : 0;
    const ungrounded = typeof values.ungrounded === 'number' ? values.ungrounded : 0;
    const total = grounded + weak + ungrounded;
    if (total <= 0) {
      return undefined;
    }
    return Math.max(0, Math.min(1, (grounded + weak * 0.5) / total));
  }, []);

  const normalizePlanStepStatus = useCallback(
    (status: unknown): RetrievalPlanStateStep['status'] => {
      const value = typeof status === 'string' ? status.toLowerCase() : 'pending';
      if (value === 'in_progress') {
        return 'running';
      }
      if (value === 'succeeded') {
        return 'completed';
      }
      if (
        value === 'pending' ||
        value === 'running' ||
        value === 'completed' ||
        value === 'failed' ||
        value === 'blocked' ||
        value === 'skipped'
      ) {
        return value;
      }
      return 'pending';
    },
    [],
  );

  const toRetrievalPlan = useCallback(
    (response: AgentPlanResponse, query: string): RetrievalPlanState => {
      const rawSteps = response.plan?.steps ?? response.steps ?? [];
      const steps: RetrievalPlanStateStep[] = rawSteps.map(
        (step: AgentPlanStep, index: number) => ({
          id: step.id || `step-${index + 1}`,
          type: step.type || 'tool_invocation',
          description: step.description || 'Planned step',
          status: normalizePlanStepStatus(step.status),
          resultSummary:
            (step.metadata?.result_summary as string | undefined) ??
            (step as unknown as { result?: { summary?: string } }).result?.summary ??
            null,
          metadata: step.metadata ?? {},
        }),
      );
      return {
        id: response.plan_id,
        sessionId: streamSessionId,
        goal: response.plan?.goal || query,
        requiresApproval: Boolean(response.plan?.approval?.required ?? true),
        steps,
        updatedAt: new Date().toISOString(),
      };
    },
    [normalizePlanStepStatus, streamSessionId],
  );

  const toInterventionStepStatus = useCallback((status: string): string => {
    if (status === 'running') {
      return 'in_progress';
    }
    if (status === 'completed') {
      return 'succeeded';
    }
    return status;
  }, []);

  const executePlan = useCallback(
    async (planId: string) => {
      if (activeExecutionRef.current) {
        return;
      }
      const executionPayload = planExecutionPayloadRef.current[planId] || {
        workspaceId: 'default',
        documentIds: [],
        retrievalLimit: 20,
      };

      const assistantMessageId = `assistant-stream-${Date.now()}`;
      let streamedText = '';
      let streamedCitations: AgentCitation[] = [];
      let streamedKnowledgeGaps: string[] = [];
      let streamedConfidence: number | undefined;
      let streamError: string | null = null;

      const abortController = new AbortController();
      activeExecutionRef.current = { planId, abortController };
      setIsLoading(true);
      setError(null);
      setExecutionPhase('executing');
      setConversation((prev) => ({
        ...prev,
        messages: [
          ...prev.messages,
          {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            timestamp: new Date().toISOString(),
            citations: [],
            knowledgeGaps: [],
          },
        ],
      }));

      try {
        await executeAgentPlanStream(
          planId,
          {
            workspace_id: executionPayload.workspaceId,
            document_ids: executionPayload.documentIds,
            retrieval_limit: executionPayload.retrievalLimit,
          },
          {
            signal: abortController.signal,
            onEvent: (event: AgentChatStreamEvent) => {
              ingestEvent(event);
              const eventType = event.event_type ?? event.event;

              if (eventType === 'error') {
                streamError = String(event.data?.error ?? event.message ?? 'stream_failed');
                return;
              }

              if (eventType === 'text_delta') {
                const delta = String(event.data?.text ?? event.data?.text_delta ?? '');
                streamedText += delta;
              }

              if (eventType === 'citation') {
                const citationRaw = (event.data?.citation ?? null) as unknown;
                const citation = normalizeStreamCitation(citationRaw as Record<string, unknown>);
                if (citation) {
                  streamedCitations = [...streamedCitations, citation];
                }
              }

              if (eventType === 'gap') {
                const gap = normalizeGap(event.data?.gap ?? event.data);
                if (gap) {
                  streamedKnowledgeGaps = [...streamedKnowledgeGaps, gap];
                }
              }

              if (eventType === 'verification') {
                const verification = event.data?.verification as { passed?: boolean } | undefined;
                if (verification && typeof verification.passed === 'boolean') {
                  streamedConfidence = verification.passed ? 0.85 : 0.45;
                } else if (typeof event.data?.passed === 'boolean') {
                  streamedConfidence = event.data.passed ? 0.85 : 0.45;
                }
              }

              if (eventType === 'complete') {
                const generated = (event.data?.generated ?? null) as Record<string, unknown> | null;
                if (generated && typeof generated.content === 'string') {
                  streamedText = generated.content;
                }
                if (generated && Array.isArray(generated.citations)) {
                  streamedCitations = generated.citations
                    .map((item) => normalizeStreamCitation(item))
                    .filter(Boolean) as AgentCitation[];
                }
                const generatedScore = scoreFromConfidenceSummary(generated?.confidence_summary);
                if (typeof generatedScore === 'number') {
                  streamedConfidence = generatedScore;
                }
                const actionPayload =
                  (event.data?.action as AgentAction | undefined) ||
                  (generated?.action as AgentAction | undefined);
                if (actionPayload) {
                  processAgentAction(actionPayload, streamedCitations);
                }
              }

              setConversation((prev) => ({
                ...prev,
                messages: prev.messages.map((message) =>
                  message.id === assistantMessageId
                    ? {
                        ...message,
                        content: streamedText || message.content,
                        citations: streamedCitations,
                        knowledgeGaps: streamedKnowledgeGaps.map((gapText) => ({
                          topic: 'Missing context',
                          why_needed: gapText,
                          suggested_source: 'user',
                        })),
                        overallConfidence: streamedConfidence,
                      }
                    : message,
                ),
              }));
            },
          },
        );

        if (streamError) {
          throw new Error(streamError);
        }

        const latestPlan = await getAgentPlan(planId);
        setPlan(
          toRetrievalPlan(
            latestPlan,
            retrievalState.activeQuery || latestPlan.plan?.goal || 'Assist user request',
          ),
          { openOverlay: false },
        );
      } catch (err) {
        console.error('Agent plan execution failed:', err);
        setExecutionPhase('failed');
        setError(err instanceof Error ? err.message : 'Failed to execute plan');
      } finally {
        activeExecutionRef.current = null;
        setIsLoading(false);
        invalidateUsageQueries();
      }
    },
    [
      ingestEvent,
      invalidateUsageQueries,
      normalizeGap,
      normalizeStreamCitation,
      retrievalState.activeQuery,
      scoreFromConfidenceSummary,
      setExecutionPhase,
      setPlan,
      toRetrievalPlan,
    ],
  );

  useEffect(() => {
    const pending = retrievalState.pendingPlanCommand;
    if (!pending || isPlanCommandRunning) {
      return;
    }
    const planId = retrievalState.plan?.id || conversation.planId;
    if (!planId) {
      acknowledgePlanCommand(pending.id);
      return;
    }

    let shouldAcknowledge = true;
    setIsPlanCommandRunning(true);

    const run = async () => {
      try {
        if (pending.type === 'execute') {
          shouldAcknowledge = false;
          acknowledgePlanCommand(pending.id);
          setIsPlanCommandRunning(false);
          await executePlan(planId);
          return;
        }

        if (pending.type === 'save_plan') {
          const steps = (retrievalState.plan?.steps ?? []).map((step, index) => ({
            id: step.id || `step-${index + 1}`,
            type: step.type || 'tool_invocation',
            description: step.description,
            status: toInterventionStepStatus(step.status),
            metadata: step.metadata ?? {},
          }));
          const updated = await interveneAgent({
            plan_id: planId,
            session_id: streamSessionId ?? retrievalState.sessionId ?? undefined,
            action: 'modify_plan',
            data: { steps },
          });
          setPlan(
            toRetrievalPlan(updated, retrievalState.activeQuery || updated.plan?.goal || ''),
            {
              openOverlay: false,
            },
          );
          setExecutionPhase('paused');
          return;
        }

        if (pending.type === 'pause' || pending.type === 'resume' || pending.type === 'stop') {
          const action = pending.type;
          const response = await interveneAgent({
            plan_id: planId,
            session_id: streamSessionId ?? retrievalState.sessionId ?? undefined,
            action,
          });
          setPlan(
            toRetrievalPlan(response, retrievalState.activeQuery || response.plan?.goal || ''),
            {
              openOverlay: false,
            },
          );
          if (action === 'pause') {
            setExecutionPhase('paused');
          } else if (action === 'resume') {
            setExecutionPhase('executing');
          } else {
            setExecutionPhase('failed');
          }
        }
      } catch (commandError) {
        console.error('Plan command failed:', commandError);
        setError(commandError instanceof Error ? commandError.message : 'Plan command failed');
      } finally {
        if (shouldAcknowledge) {
          acknowledgePlanCommand(pending.id);
        }
        if (shouldAcknowledge) {
          setIsPlanCommandRunning(false);
        }
      }
    };

    void run();
  }, [
    acknowledgePlanCommand,
    conversation.planId,
    executePlan,
    isPlanCommandRunning,
    retrievalState.activeQuery,
    retrievalState.pendingPlanCommand,
    retrievalState.plan?.id,
    retrievalState.plan?.steps,
    retrievalState.sessionId,
    setExecutionPhase,
    setPlan,
    streamSessionId,
    toInterventionStepStatus,
    toRetrievalPlan,
  ]);

  useEffect(() => {
    return () => {
      if (activeExecutionRef.current) {
        activeExecutionRef.current.abortController.abort();
      }
    };
  }, []);

  const handleSubmit = async () => {
    const trimmed = inputValue.trim();
    const question = trimmed || (attachedArtifacts.length > 0 ? 'Analyze these documents' : '');

    if (!question || isLoading) return;

    const currentAttachments = [...attachedArtifacts];
    setInputValue('');
    setAttachedArtifacts([]);
    setError(null);
    setIsLoading(true);
    startQuery(question);

    // Optimistic update - add user message
    const userMessage: WorkspaceMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content:
        trimmed ||
        (currentAttachments.length > 0
          ? `[Attached ${currentAttachments.length} document(s)]`
          : ''),
      timestamp: new Date().toISOString(),
    };

    const optimisticMessages = [...conversation.messages, userMessage];
    setConversation({ ...conversation, messages: optimisticMessages });

    try {
      let activeSessionId = streamSessionId;
      if (!activeSessionId) {
        const created = await createAgentSession({ workspace_id: 'default' });
        activeSessionId = created.session_id;
        setStreamSessionId(created.session_id);
      }
      setRetrievalSessionId(activeSessionId);

      const planResponse = await createAgentPlan({
        query: question,
        workspace_id: 'default',
        session_id: activeSessionId || undefined,
        document_ids: currentAttachments.map((item) => item.id),
        retrieval_limit: 20,
        metadata: {
          open_artifact_ids: workspaceState.openArtifactIds ?? [],
          active_artifact_id: workspaceState.activeArtifactId ?? null,
        },
      });

      planExecutionPayloadRef.current[planResponse.plan_id] = {
        workspaceId: 'default',
        documentIds: currentAttachments.map((item) => item.id),
        retrievalLimit: 20,
      };

      setPlan(toRetrievalPlan(planResponse, question), { openOverlay: false });
      setExecutionPhase('planned');

      setConversation((prev) => ({
        ...prev,
        planId: planResponse.plan_id,
        messages: optimisticMessages,
      }));

      // Invalidate usage queries to refresh the timeline after receiving a response
      invalidateUsageQueries();
    } catch (err) {
      console.error('Agent query failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to get response');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleClearChat = () => {
    setConversation({ planId: null, messages: [] });
    resetRetrievalState({ keepSession: true });
  };

  // Don't show collapsed state on mobile - the view toggle handles it
  if (isCollapsed && !isMobile) {
    return (
      <div className="w-12 h-full bg-white border-l border-neutral-200 flex flex-col items-center py-4">
        <button
          type="button"
          onClick={onToggle}
          className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-100 transition-colors"
          aria-label="Expand chat"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </button>
        {conversation.messages.length > 0 && (
          <span className="mt-2 text-[10px] text-neutral-500">{conversation.messages.length}</span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${isMobile ? 'w-full' : 'w-80'} h-full bg-white border-l border-neutral-200 flex flex-col relative lg:w-80`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 lg:px-3 lg:py-2 border-b border-neutral-100">
        <div className="flex items-center gap-2">
          <span className="font-medium text-neutral-800 text-base lg:text-sm">Assistant</span>
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${streamStatusClasses}`}
            title={retrievalState.lastError || undefined}
          >
            {streamStatusLabel}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {conversation.messages.length > 0 && (
            <button
              type="button"
              onClick={handleClearChat}
              className="p-2 lg:p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
              aria-label="Clear chat"
            >
              <svg
                className="w-5 h-5 lg:w-4 lg:h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
          {/* Only show collapse toggle on desktop */}
          {!isMobile && (
            <button
              type="button"
              onClick={onToggle}
              className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded transition-colors"
              aria-label="Collapse chat"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 lg:p-3 space-y-4 lg:space-y-3">
        {retrievalState.plan ? (
          <section className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                  Execution Plan
                </p>
                <p className="mt-0.5 truncate text-sm font-medium text-neutral-900">
                  {retrievalState.plan.goal}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPlanCardExpanded((prev) => !prev)}
                className="rounded border border-neutral-200 px-2 py-1 text-[11px] text-neutral-600 hover:bg-neutral-100"
              >
                {planCardExpanded ? 'Collapse' : 'Expand'}
              </button>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-200">
              <div
                className="h-full rounded-full bg-blue-500 transition-all"
                style={{ width: `${planProgress.percent}%` }}
              />
            </div>
            <div className="mt-1.5 flex items-center justify-between text-[11px] text-neutral-500">
              <span>
                {planProgress.completed}/{planProgress.total} steps complete
              </span>
              <span className="capitalize">{retrievalState.executionPhase}</span>
            </div>

            {planCardExpanded ? (
              <div className="mt-2 space-y-2">
                {retrievalState.plan.steps.map((step) => (
                  <div
                    key={step.id}
                    className="rounded border border-neutral-200 bg-white px-2.5 py-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-xs font-medium text-neutral-800">
                        {step.description}
                      </p>
                      <span className="text-[10px] uppercase tracking-wide text-neutral-500">
                        {step.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-2 pt-1">
                  <div className="flex items-center gap-2">
                    {retrievalState.executionPhase === 'planned' ? (
                      <button
                        type="button"
                        onClick={() => {
                          requestPlanCommand('execute');
                          setExecutionPhase('executing');
                        }}
                        className="rounded bg-blue-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-blue-700"
                      >
                        Execute
                      </button>
                    ) : retrievalState.executionPhase === 'executing' ? (
                      <button
                        type="button"
                        onClick={() => {
                          requestPlanCommand('pause');
                          setExecutionPhase('paused');
                        }}
                        className="rounded border border-neutral-200 px-2.5 py-1 text-[11px] text-neutral-700 hover:bg-neutral-100"
                      >
                        Pause
                      </button>
                    ) : retrievalState.executionPhase === 'paused' ? (
                      <button
                        type="button"
                        onClick={() => {
                          requestPlanCommand('resume');
                          setExecutionPhase('executing');
                        }}
                        className="rounded border border-neutral-200 px-2.5 py-1 text-[11px] text-neutral-700 hover:bg-neutral-100"
                      >
                        Resume
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        window.dispatchEvent(
                          new CustomEvent('curious:insights-open', { detail: { tab: 'plan' } }),
                        )
                      }
                      className="rounded border border-neutral-200 px-2.5 py-1 text-[11px] text-neutral-700 hover:bg-neutral-100"
                    >
                      Open in Insights
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {conversation.messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 lg:w-12 lg:h-12 rounded-2xl bg-neutral-50 flex items-center justify-center mb-4 border border-neutral-100 shadow-sm">
              <svg
                className="w-8 h-8 lg:w-6 lg:h-6 text-neutral-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <p className="text-base lg:text-sm font-medium text-neutral-700 mb-1">
              Ask the Assistant
            </p>
            <p className="text-sm lg:text-xs text-neutral-500 mb-6">
              Create documents, draft emails, or analyze your knowledge base.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                type="button"
                onClick={() => setInputValue('Count the PDF files in my workspace')}
                className="px-4 py-2.5 lg:px-3 lg:py-1.5 text-sm lg:text-xs font-medium text-neutral-600 bg-white border border-neutral-200 rounded-full hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 shadow-sm active:scale-95"
              >
                Count PDF files
              </button>
              <button
                type="button"
                onClick={() => setInputValue('Draft a weekly report')}
                className="px-4 py-2.5 lg:px-3 lg:py-1.5 text-sm lg:text-xs font-medium text-neutral-600 bg-white border border-neutral-200 rounded-full hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 shadow-sm active:scale-95"
              >
                Draft a report
              </button>
            </div>
          </div>
        ) : (
          conversation.messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              artifacts={workspaceState.artifacts}
              openArtifact={openArtifact}
              navigate={navigate}
              isLoading={
                isLoading && message === conversation.messages[conversation.messages.length - 1]
              }
              onCitationClick={async (citation) => {
                if (citation.path.startsWith('artifact:')) {
                  const normalizedId = normalizeArtifactId(citation.path);
                  openArtifact(normalizedId);
                  // Navigate with line number and text for precise highlighting
                  const params = new URLSearchParams();
                  if (citation.start_line) params.set('line', String(citation.start_line));
                  if (citation.end_line) params.set('endLine', String(citation.end_line));
                  if (citation.text) params.set('text', encodeURIComponent(citation.text));
                  const paramString = params.toString() ? `&${params.toString()}` : '';
                  navigate(`/?artifact=${normalizedId}${paramString}`);
                  refreshArtifacts();
                } else {
                  if (!citation.path.includes('/')) {
                    focusCitationRef(citation.path);
                    return;
                  }
                  // For indexed documents, load content and create/open artifact
                  try {
                    const docContent = await getDocumentContent(citation.path);
                    const docTitle = citation.path.split('/').pop() || citation.path;

                    // Check if artifact already exists with this title
                    const existingArtifact = workspaceState.artifacts.find(
                      (a) => a.title === docTitle && a.type === 'document',
                    );

                    const params = new URLSearchParams();
                    if (citation.start_line) params.set('line', String(citation.start_line));
                    if (citation.end_line) params.set('endLine', String(citation.end_line));
                    if (citation.text) params.set('text', encodeURIComponent(citation.text));
                    const paramString = params.toString() ? `&${params.toString()}` : '';

                    if (existingArtifact) {
                      // Open existing artifact
                      openArtifact(existingArtifact.id);
                      navigate(`/?artifact=${existingArtifact.id}${paramString}`);
                    } else {
                      // Create new artifact from document content
                      const newArtifact = await createDocument(
                        docTitle,
                        docContent.content,
                        'agent',
                      );
                      openArtifact(newArtifact.id);
                      navigate(`/?artifact=${newArtifact.id}${paramString}`);
                    }
                  } catch (error) {
                    console.error('Failed to load document:', error);
                    // Fallback to showing citation panel if loading fails
                    setOpenCitation(citation);
                  }
                }
              }}
              onFocusCitationRef={focusCitationRef}
            />
          ))
        )}
        {isLoading && (
          <div className="flex justify-start">
            <AssistantStatusIndicator
              active
              density="compact"
              prefixLabel="Assistant"
              className="px-2 py-1"
            />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-3 mb-2 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg">
          {error}
        </div>
      )}

      {/* Input */}
      <div className="p-4 lg:p-3 border-t border-neutral-100 bg-white/50 backdrop-blur-sm">
        {/* Attached Artifacts */}
        {attachedArtifacts.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {attachedArtifacts.map((artifact, idx) => (
              <div
                key={idx}
                className="flex items-center gap-1.5 px-3 py-1.5 lg:px-2 lg:py-1 bg-neutral-100 border border-neutral-200 rounded-lg text-sm lg:text-xs text-neutral-700"
              >
                <svg
                  className="w-4 h-4 lg:w-3 lg:h-3 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span className="truncate max-w-[180px] lg:max-w-[140px]">{artifact.title}</span>
                <button
                  type="button"
                  onClick={() => removeAttachment(idx)}
                  className="p-1 lg:p-0.5 text-neutral-400 hover:text-red-500 rounded-full hover:bg-neutral-200 transition-colors"
                  aria-label="Remove attachment"
                >
                  <svg
                    className="w-4 h-4 lg:w-3 lg:h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="relative">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            className="w-full px-5 py-3 lg:px-4 lg:py-2.5 pl-12 pr-12 lg:pl-10 lg:pr-10 text-base lg:text-sm text-neutral-800 bg-neutral-50/50 hover:bg-neutral-50 border border-neutral-200 focus:border-blue-400/50 rounded-xl resize-none outline-none focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 shadow-sm min-h-[48px] lg:min-h-[40px] max-h-[150px] lg:max-h-[120px] overflow-y-auto"
            placeholder="Ask anything..."
            disabled={isLoading}
          />

          <button
            type="button"
            onClick={() => setShowArtifactPicker(!showArtifactPicker)}
            className={`absolute left-3 top-3 lg:left-2 lg:top-2.5 p-2 lg:p-1.5 rounded-lg transition-colors duration-200 ${
              showArtifactPicker
                ? 'text-blue-600 bg-blue-50'
                : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100'
            }`}
            title="Attach existing document"
          >
            <svg
              className="w-5 h-5 lg:w-4 lg:h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
          </button>

          {showArtifactPicker && (
            <div
              ref={pickerRef}
              className="absolute left-0 bottom-full mb-2 w-72 bg-white rounded-lg shadow-xl border border-neutral-200 max-h-64 flex flex-col z-50 overflow-hidden"
            >
              <div className="p-2 border-b border-neutral-100">
                <input
                  type="text"
                  placeholder="Search documents..."
                  className="w-full px-2 py-1.5 text-xs bg-neutral-50 border border-neutral-200 rounded focus:border-blue-400 focus:outline-none"
                  value={artifactSearch}
                  onChange={(e) => setArtifactSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="flex-1 overflow-y-auto p-1">
                {workspaceState.artifacts
                  .filter(
                    (a) =>
                      a.type === 'document' &&
                      a.title.toLowerCase().includes(artifactSearch.toLowerCase()),
                  )
                  .map((artifact) => (
                    <button
                      key={artifact.id}
                      onClick={() => handleArtifactSelect(artifact)}
                      className="w-full text-left px-2 py-1.5 text-xs text-neutral-700 hover:bg-blue-50 hover:text-blue-700 rounded flex items-center gap-2 truncate"
                    >
                      <svg
                        className="w-3 h-3 flex-shrink-0 text-neutral-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="truncate flex-1">{artifact.title}</span>
                      {artifact.access_level && artifact.access_level !== 'owner' && (
                        <svg
                          className="w-3 h-3 flex-shrink-0 text-neutral-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          role="img"
                        >
                          <title>{artifact.access_level === 'edit' ? 'Shared (can edit)' : 'Shared (view only)'}</title>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                          />
                        </svg>
                      )}
                    </button>
                  ))}
                {workspaceState.artifacts.filter(
                  (a) =>
                    a.type === 'document' &&
                    a.title.toLowerCase().includes(artifactSearch.toLowerCase()),
                ).length === 0 && (
                  <div className="px-2 py-2 text-xs text-neutral-400 text-center">
                    No documents found
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={(!inputValue.trim() && attachedArtifacts.length === 0) || isLoading}
            className="absolute right-3 top-3 lg:right-2 lg:top-2.5 p-2 lg:p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
            aria-label="Send message"
          >
            <svg
              className="w-5 h-5 lg:w-4 lg:h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </div>
        <p className="mt-2 text-xs lg:text-[10px] text-neutral-400 text-center font-medium tracking-wide opacity-60">
          <span className="lg:hidden">Tap send to submit</span>
          <span className="hidden lg:inline">Press Enter to send · Shift+Enter for new line</span>
        </p>
      </div>
    </div>
  );
};

// Compact message bubble component
interface MessageBubbleProps {
  message: WorkspaceMessage;
  isLoading?: boolean;
  onCitationClick?: (citation: AgentCitation) => void;
  onFocusCitationRef?: (citationRef: string | null) => void;
  artifacts?: Array<{ id: string; title: string; type?: string }>;
  openArtifact?: (id: string) => void;
  navigate?: (path: string) => void;
}

// Convert [ref:...] and [NEEDS INPUT:...] markers to inline code spans.
// We intentionally avoid markdown link syntax ([text](url)) because remark's
// URL sanitiser strips non-http(s) schemes such as curious://, resulting in
// an empty href that opens a new tab.  Inline code spans are safe: remark
// never sanitises their content, and we can intercept them in MarkdownContent's
// `code` component renderer.
const toInteractiveMessageContent = (content: string): string => {
  if (!content) {
    return content;
  }

  let citationIndex = 0;
  const withCitations = content.replace(/\[ref:([^\]]+)\]/g, (_match, citationRef: string) => {
    citationIndex += 1;
    return `\`curious-citation:${citationIndex}:${citationRef.trim()}\``;
  });

  return withCitations.replace(
    /\[NEEDS INPUT:\s*([^\]]+)\]/g,
    (_match, description: string) => {
      return `\`curious-needs-input:${description.trim()}\``;
    },
  );
};

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  onCitationClick,
  onFocusCitationRef,
  artifacts = [],
  openArtifact,
  navigate,
}) => {
  const isUser = message.role === 'user';
  const hasGrounding =
    (message.groundedClaims?.length || 0) > 0 ||
    (message.knowledgeGaps?.length || 0) > 0 ||
    (message.clarifyingQuestions?.length || 0) > 0 ||
    typeof message.overallConfidence === 'number';
  // Initialize expanded state - default to true for new messages, false for older ones
  const [sourcesExpanded, setSourcesExpanded] = useState(() => {
    // Check if this is a recent message (within last 5 minutes)
    const messageTime = new Date(message.timestamp).getTime();
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    return messageTime > fiveMinutesAgo;
  });
  const [artifactsExpanded, setArtifactsExpanded] = useState(() => {
    const messageTime = new Date(message.timestamp).getTime();
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    return messageTime > fiveMinutesAgo;
  });

  const normalizeArtifactId = (raw: string) =>
    raw.replace(/^artifact:/, '').replace(/:[0-9]+$/, '');

  const getCitationLabel = (citation: AgentCitation): { title: string; lineNumber?: number } => {
    // If citation path is an artifact ID, look up the title
    if (citation.path.startsWith('artifact:')) {
      const normalizedId = normalizeArtifactId(citation.path);
      const artifact = artifacts.find((a) => {
        const normalizedArtifactId = normalizeArtifactId(a.id);
        return normalizedArtifactId === normalizedId || a.id === citation.path;
      });
      if (artifact) {
        return { title: artifact.title, lineNumber: citation.start_line ?? undefined };
      }
      // Fallback to path if artifact not found
      return { title: citation.path, lineNumber: citation.start_line ?? undefined };
    }

    // Otherwise, use the filename from path
    const filename = citation.path.split('/').pop() || citation.path;
    return { title: filename, lineNumber: citation.start_line ?? undefined };
  };

  const getActionLabel = (action: AgentAction): { prefix: string; title: string } => {
    const params = (action.parameters as Record<string, unknown>) || {};

    if (action.name === 'create_artifact' || action.name === 'draft_document') {
      const title = params.title || params.subject || 'document';
      return { prefix: 'Create', title: String(title) };
    }

    if (action.name === 'update_artifact') {
      const artifactId = params.artifact_id;
      if (artifactId && typeof artifactId === 'string') {
        const normalizedId = normalizeArtifactId(artifactId);
        const artifact = artifacts.find((a) => {
          const normalizedArtifactId = normalizeArtifactId(a.id);
          return normalizedArtifactId === normalizedId || a.id === artifactId;
        });
        if (artifact) {
          return { prefix: 'Update', title: artifact.title };
        }
      }
      const title = params.title || 'document';
      return { prefix: 'Update', title: String(title) };
    }

    if (action.name === 'read_artifact' || action.name === 'open_artifact') {
      const artifactId = params.artifact_id;
      if (artifactId && typeof artifactId === 'string') {
        const normalizedId = normalizeArtifactId(artifactId);
        const artifact = artifacts.find((a) => {
          const normalizedArtifactId = normalizeArtifactId(a.id);
          return normalizedArtifactId === normalizedId || a.id === artifactId;
        });
        if (artifact) {
          return { prefix: 'Read', title: artifact.title };
        }
      }
      return { prefix: 'Read', title: 'document' };
    }

    if (
      action.name === 'create_email' ||
      action.name === 'draft_email' ||
      action.name === 'send_email'
    ) {
      const subject = params.subject || 'email';
      return { prefix: 'Email', title: String(subject) };
    }

    if (action.name === 'update_email') {
      const subject = params.subject || 'email';
      return { prefix: 'Update Email', title: String(subject) };
    }

    if (action.name === 'database') {
      const operation = params.operation || 'query';
      const target = params.table || (params.query ? 'custom query' : '');
      const title = target ? `${operation}: ${target}` : String(operation);
      return { prefix: 'Database', title };
    }

    return { prefix: 'Action', title: action.name };
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[90%] px-4 py-3 text-sm shadow-sm border backdrop-blur-sm transition-all duration-200 rounded ${
          isUser
            ? 'bg-neutral-100/80 border-neutral-200/60 text-neutral-900 rounded-tr-sm'
            : 'bg-white/90 border-neutral-200/60 text-neutral-900 rounded-tl-sm'
        }`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>
        ) : (
          <MarkdownContent
            content={toInteractiveMessageContent(message.content)}
            className="prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 prose-p:leading-relaxed prose-li:leading-relaxed"
          />
        )}

        {/* Citations - subtle inline style */}
        {message.citations && message.citations.length > 0 && (
          <div className="mt-1.5">
            {!sourcesExpanded ? (
              <button
                type="button"
                onClick={() => setSourcesExpanded(true)}
                className="text-[10px] text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                {message.citations.length === 1
                  ? '· 1 source'
                  : `· ${message.citations.length} sources`}
              </button>
            ) : (
              <div className="mt-1 pt-1.5 border-t border-neutral-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] uppercase tracking-wider text-neutral-400">
                    Sources
                  </span>
                  <button
                    type="button"
                    onClick={() => setSourcesExpanded(false)}
                    className="text-[9px] text-neutral-400 hover:text-neutral-600"
                  >
                    hide
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {/* Deduplicate citations based on path and start_line */}
                  {Array.from(
                    new Map(
                      message.citations.map((c) => [`${c.path}:${c.start_line}`, c]),
                    ).values(),
                  ).map((citation, idx) => {
                    const label = getCitationLabel(citation);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          if (citation.path.startsWith('artifact:')) {
                            const normalizedId = normalizeArtifactId(citation.path);
                            if (openArtifact && navigate) {
                              openArtifact(normalizedId);
                              const params = new URLSearchParams();
                              if (citation.start_line)
                                params.set('line', String(citation.start_line));
                              if (citation.end_line)
                                params.set('endLine', String(citation.end_line));
                              if (citation.text)
                                params.set('text', encodeURIComponent(citation.text));
                              const paramString = params.toString() ? `&${params.toString()}` : '';
                              navigate(`/?artifact=${normalizedId}${paramString}`);
                            } else if (openArtifact) {
                              openArtifact(normalizedId);
                            }
                          } else {
                            onFocusCitationRef?.(citation.path);
                            onCitationClick?.(citation);
                          }
                        }}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700 transition-colors truncate max-w-[140px]"
                        title={`${label.title}${label.lineNumber ? ` (line ${label.lineNumber})` : ''}`}
                      >
                        {label.title}
                        {label.lineNumber ? `:${label.lineNumber}` : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {!isUser && hasGrounding ? (
          <div className="mt-2 border-t border-neutral-100 pt-2">
            <GroundingSection
              groundedClaims={message.groundedClaims}
              knowledgeGaps={message.knowledgeGaps}
              needsClarification={message.needsClarification}
              clarifyingQuestions={message.clarifyingQuestions}
              overallConfidence={message.overallConfidence}
              compact
              className="space-y-2"
              onSourceClick={(artifactId) => {
                const normalizedId = normalizeArtifactId(artifactId);
                if (openArtifact) {
                  openArtifact(normalizedId);
                }
                if (navigate) {
                  navigate(`/?artifact=${normalizedId}`);
                }
              }}
            />
          </div>
        ) : null}

        {/* Action indicator */}
        {message.action && (
          <div className="mt-2 pt-2 border-t border-neutral-200/50">
            <button
              type="button"
              onClick={() => setArtifactsExpanded(!artifactsExpanded)}
              className="flex items-center gap-1.5 w-full mb-1.5 group"
            >
              <svg
                className={`w-3 h-3 text-neutral-500 transition-transform ${artifactsExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <p className="text-[10px] uppercase tracking-wide text-neutral-500 font-medium group-hover:text-neutral-700">
                Artifacts
              </p>
            </button>
            {artifactsExpanded && (
              <button
                type="button"
                onClick={() => {
                  if (message.action && openArtifact) {
                    const params = (message.action.parameters as Record<string, unknown>) || {};
                    const artifactId = params.artifact_id;
                    const actionName = message.action.name;

                    // 1. Try opening by ID (common for read, update, open)
                    if (artifactId && typeof artifactId === 'string') {
                      const normalizedId = normalizeArtifactId(artifactId);
                      const artifact = artifacts.find((a) => {
                        const normalizedArtifactId = normalizeArtifactId(a.id);
                        return normalizedArtifactId === normalizedId || a.id === artifactId;
                      });
                      if (artifact) {
                        const id = normalizeArtifactId(artifact.id);
                        openArtifact(id);
                        if (navigate) {
                          navigate(`/?artifact=${id}`);
                        }
                        return;
                      } else {
                        // Try opening directly if not found in list (backend fetch might work)
                        openArtifact(normalizedId);
                        if (navigate) {
                          navigate(`/?artifact=${normalizedId}`);
                        }
                        return;
                      }
                    }

                    // 2. For create actions, try finding by title if ID is missing
                    if (actionName === 'create_artifact' || actionName === 'draft_document') {
                      const title = params.title;
                      if (title && typeof title === 'string') {
                        const artifact = artifacts.find((a) => a.title === title);
                        if (artifact) {
                          openArtifact(artifact.id);
                          if (navigate) {
                            navigate(`/?artifact=${artifact.id}`);
                          }
                        }
                      }
                    }
                  }
                }}
                className="group flex items-start gap-2 w-full px-3 py-2 rounded-lg bg-green-50/50 hover:bg-green-100/70 border border-green-200/50 hover:border-green-300 transition-all duration-150"
                disabled={!openArtifact}
              >
                <svg
                  className="w-3.5 h-3.5 text-green-600 shrink-0 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                <div className="flex-1 min-w-0 overflow-hidden text-left">
                  {(() => {
                    const label = getActionLabel(message.action);
                    // Check if action requires approval
                    const requiresApproval =
                      message.action.requires_approval || message.action.requiresApproval;

                    return (
                      <div className="text-[11px] text-green-700 group-hover:text-green-800">
                        <div className="font-medium mb-0.5">{label.prefix}</div>
                        {label.title && (
                          <div className="font-semibold break-words text-green-800">
                            {label.title}
                          </div>
                        )}
                        {requiresApproval && (
                          <div className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-100 text-amber-700 border border-amber-200">
                            Pending Approval
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {openArtifact && (
                  <svg
                    className="w-3 h-3 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSidebar;
